import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { COUNTRIES, countryLabel, sortCountriesByLabel } from "@/lib/countries"
import type { Locale } from "@/types"

export interface OnboardingCountrySelectProps {
  /** Rails strong-params name — e.g. `user_location[country]` or
   *  `shipping_address[country]`. Drives the hidden `<input>` that
   *  carries the selected ISO-2 value into the form submission. */
  name: string
  /** DOM id on the trigger — paired with the parent `<Label htmlFor>`. */
  id: string
  /** Stable test selector. */
  testId: string
  /** Pre-fill from BE-echoed form_values (ISO-2 string or empty). */
  defaultValue?: string
  /** Inertia render-prop error array for this field; switches the
   *  trigger's `aria-invalid` state. */
  errorMessages?: string[]
  /** DOM id of the corresponding `<InputError>` (when present); wired
   *  into the trigger's `aria-describedby`. */
  errorId?: string
  /** Forwarded to the trigger. Mirrors the parent `<Form>`'s
   *  `processing` flag. */
  disabled?: boolean
  /** Required at the form level (UX Spec §2 — both country fields are
   *  required except when explicitly noted otherwise). */
  required?: boolean
  /** Locale for label rendering. Falls back to the i18n instance's
   *  resolved language; explicit override exists for tests. */
  locale?: Locale
}

/**
 * OnboardingCountrySelect — shadcn `<Select>` (Radix-backed) wrapping a
 * hidden `<input>` that carries the selected ISO-3166-1 alpha-2 code
 * into the native form submission.
 *
 * Why the hidden-input pattern:
 *
 *   - shadcn `<Select>` (Radix Primitives) does NOT render a native
 *     `<select>` or expose a `name` attribute on a form control. It
 *     produces accessible button-trigger + portal-content markup. The
 *     selected value lives in component state.
 *   - Inertia `<Form>` serialises native form controls (`<input>`,
 *     `<select>`, `<textarea>`) with `name` attributes — so without a
 *     bridge, the Radix selection never reaches Rails.
 *   - The hidden input mirrors the controlled state, gets a `name`, and
 *     ships with the form submission. `required` propagates to it so
 *     browser-native form validation still fires for empty submits.
 *
 * **Wire contract** (BE TRD + Vitest canary):
 *   - Submitted value MUST match `^[A-Z]{2}$` (BE CHECK constraint).
 *   - Falsy default → empty hidden value, browser validates via `required`.
 *   - Display label is locale-aware via `Intl.DisplayNames` so we don't
 *     ship +250 × 2-locale name translations by hand.
 *
 * **A11y:**
 *   - Trigger carries `id={id}` + `data-testid={testId}` so the
 *     parent `<Label htmlFor>` association is intact.
 *   - `aria-invalid` flips on error; `aria-describedby` links to the
 *     `<InputError>` block when present.
 *   - shadcn Select (Radix) ships full keyboard support out of the box
 *     (Tab focus, Enter / Space to open, arrow keys to navigate, Esc to
 *     close, type-ahead).
 */
export function OnboardingCountrySelect({
  name,
  id,
  testId,
  defaultValue = "",
  errorMessages,
  errorId,
  disabled = false,
  required = true,
  locale,
}: OnboardingCountrySelectProps) {
  const { i18n, t } = useTranslation()
  const resolvedLocale: Locale =
    locale ?? (i18n.language?.split("-")[0] as Locale) ?? "fr"

  const [value, setValue] = useState<string>(defaultValue)

  const sorted = useMemo(
    () => sortCountriesByLabel(COUNTRIES, resolvedLocale),
    [resolvedLocale],
  )

  const isInvalid = errorMessages && errorMessages.length > 0

  return (
    <>
      <Select
        items={sorted.map((c) => ({
          value: c.code,
          label: countryLabel(c.code, resolvedLocale),
        }))}
        value={value}
        onValueChange={(next) => setValue(next ?? "")}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          data-testid={testId}
          // OKS-728 — required-ness is announced via `aria-required` on the
          // trigger (Option-A posture), replacing the dead native `required`
          // that used to sit on the hidden input below.
          aria-required={required ? "true" : undefined}
          aria-invalid={isInvalid ? "true" : undefined}
          aria-describedby={isInvalid && errorId ? errorId : undefined}
          className="w-full"
        >
          <SelectValue placeholder={t("signup.account.country_placeholder")} />
        </SelectTrigger>
        <SelectContent data-testid={`${testId}-content`}>
          {sorted.map((c) => (
            <SelectItem
              key={c.code}
              value={c.code}
              data-testid={`${testId}-option-${c.code}`}
            >
              {countryLabel(c.code, resolvedLocale)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/*
        Hidden input bridges the Radix-controlled state into the native
        form payload. `name` is the Rails strong-params key; `value` is
        the ISO-2 code (always — the wire-format canary verifies this).
      */}
      {/*
        Hidden input bridges the Base UI-controlled state into the native
        form payload. `name` is the Rails strong-params key; `value` is the
        ISO-2 code (always — the wire-format canary verifies this).
        OKS-728 — no native `required` here: hidden inputs are barred from
        HTML constraint validation (so it never produced a bubble anyway),
        and the required posture is now `aria-required` on the trigger +
        server-side presence on `ShippingAddress#country`.
      */}
      <input
        type="hidden"
        name={name}
        value={value}
        data-testid={`${testId}-hidden`}
      />
    </>
  )
}

export default OnboardingCountrySelect
